import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/system';
import { SingularStationArea } from '@thive/fddm-types';

import { Accordion } from '@common/components/Accordion';
import { LoadType } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/singularStation/LoadType';
import { LoadHandlingPoiSpec } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/singularStation/LoadHandlingPoiSpec';
import { ConstellationSpec } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/common/ConstellationSpec';
import { VehicleType } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/common/VehicleType';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';

import { AlwaysFitStation } from './singularStation/AlwaysFitStation';
import { CarrierSide } from './singularStation/CarrierSide';
import { LoadHandlingType } from './singularStation/LoadHandlingType';
import { StationSpecProperties } from './singularStation/StationSpecProperties';
import { SingularStationPoiPreferences } from './singularStation/SingularStationPoiPreferences';
import { SingularConnectNeighbors } from './singularStation/SingularConnectNeighbors';
import { UserPreferenceName } from '@/modules/userPreferences';
import {
  FIELDS_PADDING_BOTTOM,
  FIELDS_SPACING,
  PANEL_ACCORDION_SX,
} from '@/modules/common/constants/propertyPanel';

interface Props {
  selectedSingularStationAreas: SingularStationArea[];
}

function SingularStationAreaParametersComponent({ selectedSingularStationAreas }: Props) {
  const { t } = useTranslation();
  const title =
    selectedSingularStationAreas.length === 1
      ? t<string>('interface:properties.singular_station_area.title', 'Singular Station Area')
      : t<string>(
          'interface:properties.singular_station_area.title_plural',
          `Singular Station Areas (${selectedSingularStationAreas.length})`,
          { amount: selectedSingularStationAreas.length },
        );

  return (
    <Accordion
      persistId={UserPreferenceName.ACCORDION_PROPERTY_PANEL_SINGULAR_STATION}
      title={title}
      unmountOnExit
      sx={PANEL_ACCORDION_SX}
    >
      <Stack direction='column' spacing={FIELDS_SPACING} pb={FIELDS_PADDING_BOTTOM}>
        <LoadHandlingType selectedSingularStationAreas={selectedSingularStationAreas} />
        <PropertiesLabel i18nkey='interface:properties.area.connect_neighbors'>
          <SingularConnectNeighbors selectedSingularStationAreas={selectedSingularStationAreas} />
        </PropertiesLabel>
        <Accordion
          persistId={
            UserPreferenceName.ACCORDION_PROPERTY_PANEL_SINGULAR_STATION_CONSTELLATION_SETTINGS
          }
          titleKey='interface:properties.sections.constellation_settings'
          unmountOnExit
        >
          <Stack direction='column' spacing={FIELDS_SPACING}>
            <ConstellationSpec
              selectedAreas={selectedSingularStationAreas}
              preferenceKey='singularStationArea.constellationSpec'
            />
          </Stack>
        </Accordion>
        <Accordion
          persistId={
            UserPreferenceName.ACCORDION_PROPERTY_PANEL_SINGULAR_STATION_LOAD_CARRIER_SETTINGS
          }
          titleKey='interface:properties.sections.load_carrier_settings'
          unmountOnExit
        >
          <Stack direction='column' spacing={FIELDS_SPACING}>
            <LoadType selectedAreas={selectedSingularStationAreas} />
            <CarrierSide selectedSingularStationAreas={selectedSingularStationAreas} />
            <AlwaysFitStation selectedSingularStationAreas={selectedSingularStationAreas} />
            <StationSpecProperties selectedSingularStationAreas={selectedSingularStationAreas} />
          </Stack>
        </Accordion>

        <Accordion
          persistId={UserPreferenceName.ACCORDION_PROPERTY_PANEL_SINGULAR_STATION_VEHICLE_SETTINGS}
          titleKey='interface:properties.sections.vehicle_settings'
          unmountOnExit
        >
          <Stack direction='column' spacing={FIELDS_SPACING}>
            <LoadHandlingPoiSpec selectedSingularStationAreas={selectedSingularStationAreas} />

            <SingularStationPoiPreferences
              selectedSingularStationAreas={selectedSingularStationAreas}
            />

            <VehicleType selectedAreas={selectedSingularStationAreas} />
          </Stack>
        </Accordion>
      </Stack>
    </Accordion>
  );
}

export const SingularStationAreaParameters = memo(SingularStationAreaParametersComponent);
